import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { AtAGlanceOutput, UseAtAGlanceOptions } from './atAGlanceTypes.ts';
import {
	atAGlanceResultCache,
	createAtAGlanceAcceptedResult,
	type AtAGlanceAcceptedResult,
} from './atAGlanceResultCache.ts';
import { useAtAGlanceRequestBody } from './useAtAGlanceRequestBody.ts';
import { createAtAGlanceRequestReporter } from './createAtAGlanceRequestReporter.ts';
import { type AtAGlanceRetryReason, clearQuotaCooldownsForTesting } from './atAGlanceRetry.ts';
import { startAtAGlanceRequest } from './atAGlanceRequest.ts';

type AtAGlanceResultState = {
	contentId: string;
	acceptedResult: AtAGlanceAcceptedResult | null;
	error: Error | null;
	retryPending?: boolean;
	retryReason?: AtAGlanceRetryReason;
};

export const clearAtAGlanceResultCacheForTesting = (): void => {
	atAGlanceResultCache.clear();
	clearQuotaCooldownsForTesting();
};

export type UseAtAGlanceResult = {
	acceptedResult?: AtAGlanceAcceptedResult;
	output: AtAGlanceOutput | null;
	/**
	 * True while we are still waiting on prerequisites or the cc-smarts response.
	 * False once we have output, have an error, or know we will not fetch (ineligible).
	 */
	loading: boolean;
	error: Error | null;
	retryPending?: boolean;
	retryReason?: AtAGlanceRetryReason;
};

/**
 * Asks cc-smarts for the Teamwork Graph "at a glance" overview and per-link
 * summaries. When enabled, waits for Appears in, Links to, and enabled Related
 * content to settle before the first request.
 *
 * The byline enables requests once its open menu has fully loaded for the current page.
 * Its dedicated bridge shares the result with the panel and modal. Closing or
 * reopening those surfaces does not cancel or restart work.
 *
 * Failures are reported and returned as `error` — the rest of the connections UI
 * must not depend on this response. Overview bullets are rendered by
 * `AtAGlanceSection`; the per-link one-liners are rendered on Related content
 * rows.
 *
 * Each logical request reports one terminal operational event —
 * succeeded, failed (with a categorised reason), or aborted (torn down in flight).
 * A cache hit fires nothing: no request was made.
 */
export const useAtAGlance = (options: UseAtAGlanceOptions): UseAtAGlanceResult => {
	const { enabled, contentId, connections } = options;
	const { cloudId } = useSessionData();
	const { locale } = useIntl();
	const { createEvent } = useConfluenceAnalyticsEvents();
	const [resultState, setResultState] = useState<AtAGlanceResultState | null>(null);
	const { requestBody, waitingOnPrereqs } = useAtAGlanceRequestBody(options, { cloudId, locale });
	const observationRef = useRef(options.observation);
	observationRef.current = options.observation;
	const inputObservation = {
		requestBody,
		waitingOnPrereqs,
		enabled,
		completedMatch: Boolean(requestBody && atAGlanceResultCache.get(requestBody)),
		documentOutcome: options.documentOutcome ?? 'unknown',
		documentReady: Boolean(options.pageAdf),
	};
	const inputObservationRef = useRef(inputObservation);
	inputObservationRef.current = inputObservation;
	useEffect(() => {
		observationRef.current?.observeInput(inputObservationRef.current);
	});

	const cachedResult = requestBody ? (atAGlanceResultCache.get(requestBody) ?? null) : null;
	const currentResultState = resultState?.contentId === contentId ? resultState : null;
	const acceptedResult = cachedResult ?? currentResultState?.acceptedResult ?? undefined;
	const output = acceptedResult?.output ?? null;
	const error = cachedResult ? null : (currentResultState?.error ?? null);

	// Raw link counts can change without changing the request body. Snapshot them when
	// the effect starts so analytics counts never restart requests.
	const linkCountsRef = useRef({ incomingLinkCount: 0, outgoingLinkCount: 0 });
	linkCountsRef.current = {
		incomingLinkCount: connections.appearsIn.links.length,
		outgoingLinkCount: connections.linksTo.links.length,
	};

	useEffect(() => {
		if (!requestBody || !cloudId || !contentId) {
			setResultState((current) =>
				current
					? {
							contentId: current.contentId,
							acceptedResult: current.acceptedResult,
							error: current.error,
						}
					: null,
			);
			return;
		}
		const cachedResult = atAGlanceResultCache.get(requestBody);
		if (cachedResult) {
			// Keep this mounted consumer stable if another page later replaces the
			// single module-level cache entry. A cache hit is not a request, so no
			// operational event fires here.
			setResultState({ contentId, acceptedResult: cachedResult, error: null });
			return;
		}

		const reporter = createAtAGlanceRequestReporter(createEvent, linkCountsRef.current);
		// Clearing output here would replace both surfaces' existing summaries with skeletons.
		setResultState((current) => ({
			contentId,
			acceptedResult: current && current.contentId === contentId ? current.acceptedResult : null,
			error: null,
		}));

		const observation = observationRef.current;
		const onObservation = observation?.startOperation(requestBody);
		const cancel = startAtAGlanceRequest(
			{ cloudId, requestBody },
			{ fetch },
			{
				onObservation,
				onSuccess: (parsed, attributes) => {
					const acceptedResult = createAtAGlanceAcceptedResult(parsed, requestBody);
					atAGlanceResultCache.set(requestBody, acceptedResult);
					setResultState({ contentId, acceptedResult, error: null });
					reporter.onSuccess(parsed, attributes);
				},
				onFailure: (failure, attributes) => {
					setResultState((current) => ({
						contentId,
						acceptedResult:
							current && current.contentId === contentId ? current.acceptedResult : null,
						error: failure,
					}));
					reporter.onFailure(failure, attributes);
				},
				onRetryState: (retryPending, retryReason) => {
					setResultState((current) => ({
						contentId,
						acceptedResult: current?.contentId === contentId ? current.acceptedResult : null,
						error: retryPending ? null : (current?.error ?? null),
						retryPending,
						retryReason,
					}));
				},
				onAborted: reporter.onAborted,
			},
		);
		return () => cancel(observation?.getCancellationReason(inputObservationRef.current));
	}, [cloudId, contentId, createEvent, requestBody]);

	const loading = Boolean(enabled && !output && !error && (waitingOnPrereqs || requestBody));
	const retryPending = cachedResult ? false : currentResultState?.retryPending;
	const retryReason = retryPending ? currentResultState?.retryReason : undefined;
	useEffect(() => {
		observationRef.current?.observeResult({
			acceptedResult,
			hasOverview: Boolean(output?.overview.length),
			status: output?.status,
			failed: Boolean(error),
			loading,
		});
	}, [acceptedResult, output, error, loading]);
	return useMemo(
		() => ({ acceptedResult, output, error, loading, retryPending, retryReason }),
		[acceptedResult, output, error, loading, retryPending, retryReason],
	);
};
